'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/platform/auth/useAuth';
import { acceptTerms } from '@/platform/users/api';

export type TermsAcceptanceFormProps = {
  /** When set (modal), skip returnTo navigation after accept. */
  onAccepted?: () => void;
};

function TermsAcceptanceFormCore({
  returnTo,
  onAccepted,
}: {
  returnTo: string;
  onAccepted?: () => void;
}) {
  const router = useRouter();
  const { refreshProfile } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!agreed) return;
    setSubmitting(true);
    setError('');
    try {
      await acceptTerms();
      await refreshProfile();
      if (onAccepted) {
        onAccepted();
        return;
      }
      router.replace(returnTo.startsWith('/') ? returnTo : '/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save acceptance');
      setSubmitting(false);
    }
  }

  return (
    <form className="terms-accept-form" onSubmit={(e) => void onSubmit(e)}>
      <label className="terms-accept-form__checkbox">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <span>
          I agree to the{' '}
          <Link href="/legal/terms/" target="_blank" rel="noopener noreferrer">
            Terms of Use
          </Link>
          ,{' '}
          <Link href="/legal/community/" target="_blank" rel="noopener noreferrer">
            Community Guidelines
          </Link>
          , and{' '}
          <Link href="/legal/privacy/" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
          ,{' '}
          <Link href="/legal/cookies/" target="_blank" rel="noopener noreferrer">
            Cookie Policy
          </Link>
          .
        </span>
      </label>
      <button type="submit" className="btn btn-primary" disabled={!agreed || submitting}>
        {submitting ? 'Saving…' : 'I agree'}
      </button>
      {error && <p className="form-error">{error}</p>}
    </form>
  );
}

function TermsAcceptanceFormFromQuery({ onAccepted }: TermsAcceptanceFormProps) {
  const searchParams = useSearchParams();
  const rawReturn = searchParams.get('returnTo');
  const returnTo = rawReturn && rawReturn.startsWith('/') ? rawReturn : '/';
  return <TermsAcceptanceFormCore returnTo={returnTo} onAccepted={onAccepted} />;
}

/** Page (query returnTo) or modal (onAccepted, no navigation). */
export function TermsAcceptanceForm({ onAccepted }: TermsAcceptanceFormProps = {}) {
  if (onAccepted) {
    return <TermsAcceptanceFormCore returnTo="/" onAccepted={onAccepted} />;
  }
  return <TermsAcceptanceFormFromQuery />;
}
